import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { IdogcamCamera } from '../model/IdogcamModel';
import IdogcamStatusBadge from '../IdogcamStatusBadge/IdogcamStatusBadge';
import styles from './IdogcamCamerasTableRow.module.scss';

interface IdogcamCamerasTableRowProps {
    camera: IdogcamCamera;
    onEdit?: (camera: IdogcamCamera) => void;
    onView?: (camera: IdogcamCamera) => void;
}

const ACCESS_TYPE_LABELS = { public: 'Public', private: 'Private' };

export const IdogcamCamerasTableRow: FunctionComponent<IdogcamCamerasTableRowProps> = ({ camera, onEdit, onView }) => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    // Freshly synced cameras have no access type until staff configures them.
    const typeLabel = camera.accessType ? ACCESS_TYPE_LABELS[camera.accessType] : '-';
    // Inactive cameras are never exposed, so there is nothing to preview.
    const canView = camera.active && !!onView;

    return (
        <TableRow>
            {!isMobile && <TableCell className={styles.idCell}>{camera.externalId}</TableCell>}
            <TableCell>
                <div className={styles.nameCell}>
                    <span className={styles.name}>{camera.name}</span>
                    {camera.addressName && (
                        <span className={styles.location}>
                            <FontAwesomeIcon icon={faLocationDot} />
                            {camera.addressName}
                        </span>
                    )}
                    {isMobile && (
                        <span className={styles.mobileInfo}>
                            ID {camera.externalId} · {typeLabel}
                        </span>
                    )}
                </div>
            </TableCell>
            {!isMobile && <TableCell>{typeLabel}</TableCell>}
            <TableCell>
                <IdogcamStatusBadge active={camera.active} />
            </TableCell>
            <TableCell isActions>
                <div className={styles.actions}>
                    <button
                        type="button"
                        aria-label={`Edit ${camera.name}`}
                        className={styles.actionButton}
                        disabled={!onEdit}
                        onClick={() => onEdit?.(camera)}
                    >
                        <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <button
                        type="button"
                        aria-label={`View ${camera.name}`}
                        className={clsx(styles.actionButton, styles.viewButton)}
                        disabled={!canView}
                        onClick={() => onView?.(camera)}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                </div>
            </TableCell>
        </TableRow>
    );
};

export default IdogcamCamerasTableRow;
